/**
 * Shared component for user information fields
 * Used in registration and profile forms
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const UserInfoFields = ({
  formData,
  onChange,
  errors = {},
  showGender = true,
  showBirthYear = true,
  showAddress = true,
  showCountry = true,
  showCity = true,
  showState = true,
  showZipCode = true,
  requiredFields = ['name', 'email', 'phone', 'city', 'zipCode', 'country'],
  countries = [],
  states = [],
  selectedCountry = '',
  loadingCountries = false,
  loadingStates = false,
  onCountryChange = null,
  onStateChange = null,
  onBlur = null,
  prefix = ''
}) => {
  const isRequired = (field) => requiredFields.includes(field);
  const fieldName = (name) => prefix ? `${prefix}-${name}` : name;
  const fieldId = (name) => prefix ? `${prefix}-${name}` : name;
  const getError = (name) => errors[fieldName(name)] || errors[name] || null;

  return (
    <>
      <div>
        <Label htmlFor={fieldId('name')} className="text-gray-900 font-semibold mb-2 block">
          Full Name {isRequired('name') && <span className="text-red-700">*</span>}
        </Label>
        <Input
          id={fieldId('name')}
          name={fieldName('name')}
          value={formData.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          onBlur={(e) => onBlur && onBlur('name', e.target.value)}
          required={isRequired('name')}
          className="border-gray-300 h-12"
        />
        {getError('name') && <p className="text-red-600 text-sm mt-1">{getError('name')}</p>}
      </div>

      <div>
        <Label htmlFor={fieldId('email')} className="text-gray-900 font-semibold mb-2 block">
          Email {isRequired('email') && <span className="text-red-700">*</span>}
        </Label>
        <Input
          id={fieldId('email')}
          name={fieldName('email')}
          type="email"
          value={formData.email || ''}
          onChange={(e) => onChange('email', e.target.value)}
          onBlur={(e) => onBlur && onBlur('email', e.target.value)}
          required={isRequired('email')}
          className="border-gray-300 h-12"
        />
        {getError('email') && <p className="text-red-600 text-sm mt-1">{getError('email')}</p>}
      </div>

      <div>
        <Label htmlFor={fieldId('phone')} className="text-gray-900 font-semibold mb-2 block">
          Phone Number {isRequired('phone') && <span className="text-red-700">*</span>}
        </Label>
        <Input
          id={fieldId('phone')}
          name={fieldName('phone')}
          type="tel"
          value={formData.phone || ''}
          onChange={(e) => onChange('phone', e.target.value)}
          onBlur={(e) => onBlur && onBlur('phone', e.target.value)}
          required={isRequired('phone')}
          className="border-gray-300 h-12"
        />
        {getError('phone') && <p className="text-red-600 text-sm mt-1">{getError('phone')}</p>}
      </div>

      {showGender && (
        <div>
          <Label id={`${fieldId('gender')}-label`} htmlFor={fieldId('gender')} className="text-gray-900 font-semibold mb-2 block">
            Gender
          </Label>
          <Select
            value={formData.gender || ''}
            onValueChange={(value) => {
              onChange('gender', value);
              if (onBlur) onBlur('gender', value);
            }}
          >
            <SelectTrigger id={fieldId('gender')} className="border-gray-300 h-12" aria-labelledby={`${fieldId('gender')}-label`}>
              <SelectValue placeholder="Select your gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
          {getError('gender') && <p className="text-red-600 text-sm mt-1">{getError('gender')}</p>}
        </div>
      )}

      {showBirthYear && (
        <div>
          <Label htmlFor={fieldId('birth-year')} className="text-gray-900 font-semibold mb-2 block">
            Birth Year
          </Label>
          <Input
            id={fieldId('birth-year')}
            name={fieldName('birth-year')}
            type="text"
            maxLength={4}
            pattern="[0-9]{4}"
            value={formData.birthYear ? String(formData.birthYear) : ''}
            onChange={(e) => {
              // Only allow numeric input
              const value = e.target.value.replace(/\D/g, '');
              // Limit to 4 digits
              const limitedValue = value.slice(0, 4);
              onChange('birthYear', limitedValue ? parseInt(limitedValue, 10) : null);
            }}
            onBlur={(e) => {
              const value = e.target.value.trim();
              onBlur && onBlur('birth-year', value ? parseInt(value, 10) : null);
            }}
            placeholder="YYYY (e.g., 1990)"
            className="border-gray-300 h-12"
          />
          {getError('birthYear') && <p className="text-red-600 text-sm mt-1">{getError('birthYear')}</p>}
        </div>
      )}

      {/* Location fields in order: Country -> State -> City -> Zip */}
      {showCountry && (
      <div>
        <Label id={`${fieldId('country')}-label`} htmlFor={fieldId('country')} className="text-gray-900 font-semibold mb-2 block">
          Country {isRequired('country') && <span className="text-red-700">*</span>}
        </Label>
        {countries.length > 0 ? (
          <Select
            value={formData.country || selectedCountry || ''}
            onValueChange={(value) => {
              onChange('country', value);
              if (onCountryChange) onCountryChange(value);
            }}
            disabled={loadingCountries}
            required={isRequired('country')}
          >
            <SelectTrigger id={fieldId('country')} className="border-gray-300 h-12" aria-labelledby={`${fieldId('country')}-label`}>
              <SelectValue placeholder={loadingCountries ? "Loading countries..." : "Select country"} />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country, index) => {
                // Safely extract country name - ensure it's always a string
                let countryName;
                if (typeof country === 'string') {
                  countryName = country;
                } else if (country && typeof country === 'object') {
                  // Handle case where name might be an object or other type
                  const nameValue = country.name;
                  if (typeof nameValue === 'string' && nameValue) {
                    countryName = nameValue;
                  } else if (nameValue != null) {
                    // If name exists but isn't a string, convert to string safely
                    countryName = String(nameValue);
                  } else {
                    // Fallback to index-based name
                    countryName = `Country ${index + 1}`;
                  }
                } else {
                  countryName = String(country || `Country ${index + 1}`);
                }

                // Safely extract country code - ensure it's always a string or null
                let countryCode = null;
                if (typeof country === 'string') {
                  countryCode = country;
                } else if (country && typeof country === 'object') {
                  const codeValue = country.code;
                  if (typeof codeValue === 'string' && codeValue) {
                    countryCode = codeValue;
                  } else if (codeValue != null) {
                    // If code exists but isn't a string, convert to string safely
                    countryCode = String(codeValue);
                  }
                }

                const countryValue = countryName;
                
                // Ensure key is always a unique string - prefer code, then name, then index
                let keyValue;
                if (countryCode && typeof countryCode === 'string') {
                  keyValue = `country-${countryCode}-${index}`;
                } else if (countryName && typeof countryName === 'string') {
                  keyValue = `country-${countryName}-${index}`;
                } else {
                  keyValue = `country-${index}`;
                }
                
                return (
                  <SelectItem key={keyValue} value={countryValue}>
                    {countryName}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={fieldId('country')}
            name={fieldName('country')}
            value={formData.country || ''}
            onChange={(e) => onChange('country', e.target.value)}
            onBlur={(e) => onBlur && onBlur('country', e.target.value)}
            required={isRequired('country')}
            className="border-gray-300 h-12"
          />
        )}
        {getError('country') && <p className="text-red-600 text-sm mt-1">{getError('country')}</p>}
      </div>
      )}

      {showState && (
      <div>
        <Label id={`${fieldId('state')}-label`} htmlFor={fieldId('state')} className="text-gray-900 font-semibold mb-2 block">
          State/Province
        </Label>
        {states.length > 0 ? (
          <Select
            value={formData.state || ''}
            onValueChange={(value) => {
              onChange('state', value);
              if (onStateChange) onStateChange(value);
            }}
            disabled={loadingStates || !selectedCountry}
          >
            <SelectTrigger id={fieldId('state')} className="border-gray-300 h-12" aria-labelledby={`${fieldId('state')}-label`}>
              <SelectValue placeholder={loadingStates ? "Loading states..." : "Select state/province"} />
            </SelectTrigger>
            <SelectContent>
              {states.map((state, index) => {
                // Safely extract state name - ensure it's always a string
                let stateName;
                if (typeof state === 'string') {
                  stateName = state;
                } else if (state && typeof state === 'object') {
                  // Handle case where state_name might be an object or other type
                  const nameValue = state.state_name;
                  if (typeof nameValue === 'string' && nameValue) {
                    stateName = nameValue;
                  } else if (nameValue != null) {
                    // If name exists but isn't a string, convert to string safely
                    stateName = String(nameValue);
                  } else {
                    // Fallback to index-based name
                    stateName = `State ${index + 1}`;
                  }
                } else {
                  stateName = String(state || `State ${index + 1}`);
                }

                // Safely extract state id - ensure it's always a string or null
                let stateId = null;
                if (typeof state === 'string') {
                  stateId = state;
                } else if (state && typeof state === 'object') {
                  const idValue = state.id;
                  if (typeof idValue === 'string' && idValue) {
                    stateId = idValue;
                  } else if (idValue != null) {
                    // If id exists but isn't a string, convert to string safely
                    stateId = String(idValue);
                  }
                }

                const stateValue = stateName;
                
                // Ensure key is always a unique string - prefer id, then name, then index
                let keyValue;
                if (stateId && typeof stateId === 'string') {
                  keyValue = `state-${stateId}-${index}`;
                } else if (stateName && typeof stateName === 'string') {
                  keyValue = `state-${stateName}-${index}`;
                } else {
                  keyValue = `state-${index}`;
                }
                
                return (
                  <SelectItem key={keyValue} value={stateValue}>
                    {stateName}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={fieldId('state')}
            name={fieldName('state')}
            value={formData.state || ''}
            onChange={(e) => onChange('state', e.target.value)}
            onBlur={(e) => onBlur && onBlur('state', e.target.value)}
            placeholder={selectedCountry ? "No states available" : "Select country first"}
            disabled={!selectedCountry}
            className="border-gray-300 h-12"
          />
        )}
        {getError('state') && <p className="text-red-600 text-sm mt-1">{getError('state')}</p>}
      </div>
      )}

      {showCity && (
      <div>
        <Label htmlFor={fieldId('city')} className="text-gray-900 font-semibold mb-2 block">
          City {isRequired('city') && <span className="text-red-700">*</span>}
        </Label>
        <Input
          id={fieldId('city')}
          name={fieldName('city')}
          value={formData.city || ''}
          onChange={(e) => onChange('city', e.target.value)}
          onBlur={(e) => onBlur && onBlur('city', e.target.value)}
          required={isRequired('city')}
          className="border-gray-300 h-12"
        />
        {getError('city') && <p className="text-red-600 text-sm mt-1">{getError('city')}</p>}
      </div>
      )}

      {showZipCode && (
      <div>
        <Label htmlFor={fieldId('zip-code')} className="text-gray-900 font-semibold mb-2 block">
          Zip/Postal Code {isRequired('zipCode') && <span className="text-red-700">*</span>}
        </Label>
        <Input
          id={fieldId('zip-code')}
          name={fieldName('zip-code')}
          value={formData.zipCode || ''}
          onChange={(e) => onChange('zipCode', e.target.value)}
          onBlur={(e) => onBlur && onBlur('zip-code', e.target.value)}
          required={isRequired('zipCode')}
          className="border-gray-300 h-12"
        />
        {getError('zipCode') && <p className="text-red-600 text-sm mt-1">{getError('zipCode')}</p>}
      </div>
      )}

      {showAddress && (
        <div>
          <Label htmlFor={fieldId('address')} className="text-gray-900 font-semibold mb-2 block">
            Address
          </Label>
          <Textarea
            id={fieldId('address')}
            name={fieldName('address')}
            value={formData.address || ''}
            onChange={(e) => onChange('address', e.target.value)}
            className="border-gray-300"
            rows={3}
          />
          {getError('address') && <p className="text-red-600 text-sm mt-1">{getError('address')}</p>}
        </div>
      )}
    </>
  );
};

export default UserInfoFields;

